import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { EVENTS, useTelemetryEmitter } from "../event-emitter"
import type { TelemetryValue } from "../event-emitter"
import { matchesExactPath } from "../navigation/navigation-utils"
import { tabsBaseItemStyles } from "../tabs-base"
import { Tooltip } from "../tooltip"
import { cn, extractText } from "../utils"
import type { StrictOmit } from "../utils"
import { useTabLinksContext } from "./tab-links-context"

// =============================================================================
// Props
// =============================================================================

export type TabLinksItemProps = {
  /**
   * Link content. Compose an `Icon` before the label or a `CountBadge` after
   * it as needed.
   */
  children?: React.ReactNode
  className?: string
  /**
   * Link href. The item is active when it matches the ancestor `TabLinks`'
   * `currentPath` exactly (a trailing slash on `href` is ignored).
   */
  href: string
  /**
   * Replace the rendered element so consumers can supply their own link
   * component (e.g. Inertia `Link`, react-router `Link`, plain `<a>`).
   * Defaults to an `<a>` when omitted.
   *
   * Pass the content as `children` of the item and a childless render element
   * (`render={<Link href="…" />}`). Children placed directly on the render
   * element take precedence over the item's `children`.
   */
  render?: useRender.RenderProp
  /**
   * Data to emit alongside the `button_click` event when this link is clicked.
   * Merges with any ancestor `Telemetry` context (local values win). Set to
   * `false` to opt this link out of emitting.
   */
  telemetry?: TelemetryValue
  /**
   * Shown on hover and keyboard focus, and announced as the link's
   * description, such as what the section holds when its label alone doesn't
   * say.
   */
  tooltip?: React.ReactNode
} & StrictOmit<
  React.ComponentPropsWithoutRef<"a">,
  // Owned by the item
  "children" | "className" | "href" | "aria-current"
>

// =============================================================================
// Component
// =============================================================================

/**
 * A tab link, rendered as an `<a>` with `aria-current="page"` when its `href`
 * matches the ancestor `TabLinks`' `currentPath`.
 *
 * Must be a descendant of `TabLinks`.
 *
 * To change the vertical padding of a horizontal link, set
 * `--tabs-item-padding-y` (e.g. `[--tabs-item-padding-y:1.25rem]`) rather than
 * a `py-*` class, so the focus ring stays around the content.
 */
export const TabLinksItem = React.forwardRef<
  HTMLAnchorElement,
  TabLinksItemProps
>(function TabLinksItem(
  { children, className, href, render, telemetry, tooltip, onClick, ...props },
  ref
) {
  const { currentPath, orientation } = useTabLinksContext()
  const isActive = matchesExactPath(currentPath, href)
  const emitTelemetry = useTelemetryEmitter(telemetry)

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    emitTelemetry(EVENTS.BUTTON_CLICK, {
      text: extractText(children),
      value: href
    })
    onClick?.(event)
  }

  const link = useRender({
    defaultTagName: "a",
    render,
    ref,
    props: {
      ...props,
      "aria-current": isActive ? ("page" as const) : undefined,
      children,
      className: cn(tabsBaseItemStyles({ orientation }), className),
      "data-slot": "tab-links-item",
      href,
      onClick: handleClick
    }
  })

  // Always wrapped, so setting or clearing `tooltip` doesn't remount the link
  // and drop its focus
  return (
    <Tooltip disabled={!tooltip}>
      <Tooltip.Trigger render={link} />
      <Tooltip.Content position={orientation === "vertical" ? "right" : "top"}>
        {tooltip}
      </Tooltip.Content>
    </Tooltip>
  )
})

TabLinksItem.displayName = "TabLinks.Item"
